import type { DiscussionPeerActivityEvent } from '@jarvis/platform-file-review';
import {
  discussionFileBasename,
  formatDiscussionPeerActivityTitle
} from './discussionNotificationCopy';

const DISCUSSION_NOTIFICATION_SW_URL = '/discussion-notifications-sw.js';
const DEFAULT_ICON_CANDIDATES = ['/img/hvg-ico.ico', '/favicon.ico'];

export type ShowSystemNotificationResult =
  | { ok: true; via: 'service-worker' | 'notification' }
  | { ok: false; reason: 'unsupported' | 'denied' | 'error'; message: string };

let cachedSwRegistration: ServiceWorkerRegistration | null | undefined;

/** @internal test helper */
export function resetDiscussionNotificationServiceWorkerCache(): void {
  cachedSwRegistration = undefined;
}

export function getSystemNotificationPermission(): NotificationPermission | 'unsupported' {
  if (typeof window === 'undefined' || typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission;
}

export async function requestSystemNotificationPermission(): Promise<NotificationPermission | 'unsupported'> {
  const current = getSystemNotificationPermission();
  if (current === 'unsupported') return 'unsupported';
  if (current !== 'default') return current;
  try {
    return await Notification.requestPermission();
  } catch {
    return Notification.permission;
  }
}

function resolveNotificationIcon(): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const link = document.querySelector<HTMLLinkElement>("link[rel='icon'], link[rel='shortcut icon']");
  const href = link?.href?.trim();
  if (href) return href;
  return DEFAULT_ICON_CANDIDATES[0];
}

/** Register (or reuse) the discussion-notifications service worker when available. */
export async function ensureDiscussionNotificationServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return null;
  if (cachedSwRegistration !== undefined) return cachedSwRegistration;
  try {
    const existing = await navigator.serviceWorker.getRegistration(DISCUSSION_NOTIFICATION_SW_URL);
    if (existing) {
      cachedSwRegistration = existing;
      return existing;
    }
    // Prefer an already-active registration for this origin (any scope).
    const any = await navigator.serviceWorker.getRegistration();
    if (any) {
      cachedSwRegistration = any;
      return any;
    }
    const registered = await navigator.serviceWorker.register(DISCUSSION_NOTIFICATION_SW_URL, {
      scope: '/'
    });
    cachedSwRegistration = registered;
    return registered;
  } catch {
    // Allow a later retry (script may not be deployed yet).
    return null;
  }
}

async function showViaServiceWorker(
  title: string,
  options: NotificationOptions
): Promise<ShowSystemNotificationResult | null> {
  const registration = await ensureDiscussionNotificationServiceWorker();
  if (!registration) return null;
  try {
    await registration.showNotification(title, options);
    return { ok: true, via: 'service-worker' };
  } catch (err) {
    return {
      ok: false,
      reason: 'error',
      message: err instanceof Error ? err.message : 'Service worker notification failed.'
    };
  }
}

function showViaNotificationConstructor(
  title: string,
  options: NotificationOptions
): Promise<ShowSystemNotificationResult> {
  return new Promise((resolve) => {
    try {
      const notification = new Notification(title, options);
      let settled = false;
      const finish = (result: ShowSystemNotificationResult) => {
        if (settled) return;
        settled = true;
        resolve(result);
      };

      notification.onclick = () => {
        window.focus();
        notification.close();
      };
      notification.onshow = () => {
        finish({ ok: true, via: 'notification' });
      };
      notification.onerror = () => {
        finish({
          ok: false,
          reason: 'error',
          message:
            'The browser accepted permission, but Windows/Edge blocked the notification banner. Check OS notification settings for Microsoft Edge.'
        });
      };

      // Some engines never fire onshow; treat construction success as delivered.
      window.setTimeout(() => {
        finish({ ok: true, via: 'notification' });
      }, 250);
    } catch (err) {
      resolve({
        ok: false,
        reason: 'error',
        message:
          err instanceof Error
            ? err.message
            : 'Failed to construct a desktop notification. A service worker may be required.'
      });
    }
  });
}

export async function showSystemNotification(
  title: string,
  options: NotificationOptions = {}
): Promise<ShowSystemNotificationResult> {
  if (typeof window === 'undefined' || typeof Notification === 'undefined') {
    return { ok: false, reason: 'unsupported', message: 'Desktop notifications are not supported in this browser.' };
  }
  if (Notification.permission !== 'granted') {
    return {
      ok: false,
      reason: 'denied',
      message: 'Desktop notification permission is not granted.'
    };
  }

  const icon = options.icon ?? resolveNotificationIcon();
  const payload: NotificationOptions = {
    ...options,
    icon,
    badge: options.badge ?? icon
  };

  const swResult = await showViaServiceWorker(title, payload);
  if (swResult?.ok) return swResult;
  // If SW path hard-failed with a message other than missing registration, still try constructor.
  const ctorResult = await showViaNotificationConstructor(title, payload);
  if (ctorResult.ok) return ctorResult;
  if (swResult && !swResult.ok) return swResult;
  return ctorResult;
}

export async function showDiscussionSystemNotification(
  event: DiscussionPeerActivityEvent
): Promise<boolean> {
  const result = await showSystemNotification(formatDiscussionPeerActivityTitle(event), {
    body: discussionFileBasename(event.path),
    tag: `discussion-${event.threadId}`,
    silent: false
  });
  return result.ok;
}

export async function showDiscussionTestSystemNotification(): Promise<ShowSystemNotificationResult> {
  return showSystemNotification('Discussion alerts enabled', {
    body: 'You will be notified when someone updates a discussion while you have a file open. If you do not see a banner, check the Windows notification center and Focus assist.',
    tag: 'discussion-test',
    requireInteraction: true,
    silent: false
  });
}
